import { Schema } from 'effect';

// DTOs for the synchronous image OCR endpoint (`POST /api/v2/ocr`). The client
// sends a single page image (PNG bytes); the server returns the recognized text
// plus per-word boxes in the pixel space of that image. Used by the Kindle overlay
// and screenshot / scan-to-listen. See docs/projects/active/kindle-integration §4.

/** Upper bound on a single OCR image upload. Guards the synchronous path. */
export const OCR_MAX_IMAGE_BYTES = 12 * 1024 * 1024;

/**
 * One recognized word, boxed in the pixel space of the submitted image (top-left
 * origin), and spanning `[char_start, char_end)` of `OcrExtractResponse.text`. The
 * server builds `text` by joining the word tokens, so these spans are exact — the
 * overlay highlights by char range without re-aligning.
 */
export const OcrWordBox = Schema.Struct({
  text: Schema.String,
  box: Schema.Tuple([
    Schema.Finite,
    Schema.Finite,
    Schema.Finite,
    Schema.Finite,
  ]).annotate({
    description: 'Pixel-space box [x1, y1, x2, y2], top-left origin.',
  }),
  char_start: Schema.Int,
  char_end: Schema.Int,
  confidence: Schema.Finite,
}).annotate({
  identifier: 'OcrWordBox',
  description:
    'A recognized word with its pixel-space box and char span into the page text.',
});
export type OcrWordBox = typeof OcrWordBox.Type;

/** Result of OCRing one page image. Pixel dimensions are those of the submitted image. */
export const OcrExtractResponse = Schema.Struct({
  text: Schema.String,
  words: Schema.Array(OcrWordBox),
  width: Schema.Int,
  height: Schema.Int,
}).annotate({
  identifier: 'OcrExtractResponse',
  description: 'Recognized page text plus per-word pixel-space boxes.',
});
export type OcrExtractResponse = typeof OcrExtractResponse.Type;
